import React, { useState } from 'react'
import { Control, Controller, RegisterOptions } from 'react-hook-form'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { ReactComponent as ChevronDown } from '../../images/icons/icon_sortdown.svg'
import { ReactComponent as ChevronUp } from '../../images/icons/icon_sortup.svg'

type DescriptionBox = {
  title: string
  value: string | number
}

type Props = {
  name: string
  label?: string
  description?: string
  control?: Control<any>
  rules?: RegisterOptions
  value: string | number
  icon?: React.ReactNode
  disabled?: boolean
  background?: 'white' | 'gray'
  descriptionBox?: DescriptionBox
  cards?: Array<JSX.Element>
  type?: 'checkbox' | 'radio'
}

const ProductCardRadioInput = ({
  name,
  label,
  description,
  value,
  control,
  rules,
  icon,
  disabled,
  background,
  descriptionBox,
  cards,
  type = 'checkbox',
}: Props) => {
  const className = classNames('transition ease-in-out duration-500', {
    'border-2 flex flex-1 p-5 flex-col justify-center rounded-lg gap-5 peer-checked:border-primary-persian-blue peer-checked:bg-primary-persian-blue/10':
      background === 'white',
    'bg-cultured-gray flex flex-1 flex-col p-5 items-center justify-center rounded-lg gap-5 peer-checked:bg-primary-persian-blue/10':
      background !== 'white',
    'cursor-pointer': !disabled,
    'text-web-gray cursor-not-allowed': disabled,
  })
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <div className={'flex'}>
          <input
            {...field}
            type={type}
            id={`${name}-${value}`}
            name={name}
            value={value}
            disabled={disabled}
            className="hidden peer"
          />
          <label htmlFor={`${name}-${value}`} className={className}>
            <div className={'flex flex-row items-center'}>
              <span className={'self-start'}>{icon}</span>
              <div className={'flex flex-col gap-2 px-3'}>
                <span className={'text-sm font-bold'}>{label}</span>
                <span className={'text-xs'}>{description}</span>
                {cards && (
                  <>
                    {isExpanded ? (
                      <button
                        className={'flex flex-row items-center gap-2 w-fit'}
                        onClick={(e) => {
                          e.preventDefault()
                          setIsExpanded(false)
                        }}>
                        <span className={'text-xs font-bold text-primary-persian-blue '}>
                          Hide ad examples
                        </span>
                        <span>
                          <ChevronUp className={'stroke-primary-persian-blue'} />
                        </span>
                      </button>
                    ) : (
                      <button
                        className={'flex flex-row items-center gap-2 w-fit'}
                        onClick={(e) => {
                          e.preventDefault()
                          setIsExpanded(true)
                        }}>
                        <span className={'text-xs font-bold text-primary-persian-blue '}>
                          Show ad examples
                        </span>
                        <span>
                          <ChevronDown className={'stroke-primary-persian-blue'} />
                        </span>
                      </button>
                    )}

                    {isExpanded && (
                      <AnimatePresence>
                        <motion.div
                          className={`flex flex-row flex-nowrap gap-8`}
                          initial={{ opacity: 0, y: -50 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            transition: { type: 'spring', damping: 20, stiffness: 150 },
                          }}
                          exit={{
                            opacity: 0,
                            y: 0,
                            transition: { duration: 2 },
                          }}>
                          {cards?.map((card) => {
                            return (
                              <div
                                key={card.key}
                                className={
                                  'py-2 flex flex-col w-full justify-center rounded-lg bg-white'
                                }>
                                {card}
                              </div>
                            )
                          })}
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </>
                )}
              </div>
            </div>
          </label>
        </div>
      )}
    />
  )
}

export default ProductCardRadioInput
